import { css, html, LitElement, type TemplateResult } from "lit";
import { SignalWatcher } from "@lit-labs/signals";
import { customElement, property } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import * as Styles from "../../styles.ts";
import { renderRoute } from "../../../util/route.ts";
import { toRelativeTime } from "../../util/date.ts";
import { type Conversation } from "../../../models/conversation.ts";
import { type AppStore, isProjectLoading } from "../app-store.ts";
import { type DropzoneDropDetail } from "../dui/dropzone/dui-dropzone.ts";
import * as Config from "../../client-config.ts";

// Components
import "../app/app-toolbar.ts";
import "../app/project-menu.ts";
import "../app/conversation-menu.ts";
import "../app/ai-box.ts";
import "../dui/page-inset/dui-page-inset.ts";
import "../dui/vstack/dui-vstack.ts";
import "../dui/dropzone/dui-dropzone.ts";
import "../dui/breadcrumb/dui-breadcrumb.ts";
import "../dui/breadcrumb/dui-breadcrumb-list.ts";
import "../dui/breadcrumb/dui-breadcrumb-item.ts";
import "../dui/breadcrumb/dui-breadcrumb-page.ts";
import "../dui/hstack/dui-hstack.ts";
import "../dui/trunc/dui-trunc.ts";
import "../dui/button/dui-button.ts";
import "../app/loading.ts";

const styles = css`
  :host {
    display: block;
    height: 100%;
    width: 100%;
    container-type: size;
  }

  .layout {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: min-content 1fr;
    grid-template-areas:
      "header"
      "main";
    height: 100cqh;
    width: 100cqw;
  }

  .layout-header {
    grid-area: header;
    overflow: hidden;
  }

  .layout-main {
    grid-area: main;
    overflow: hidden;
    container-type: size;
  }

  .project-header {
    margin-bottom: var(--space-6);
  }

  h1 {
    color: var(--foreground);
    font-weight: var(--font-weight-semibold);
    font-size: var(--font-size-2xl);
    line-height: var(--line-height-tight);
  }

  .section-label {
    color: var(--muted-foreground);
    font-size: var(--font-size-xs);
    letter-spacing: var(--letter-spacing-normal);
    font-weight: var(--font-weight-medium);
    line-height: var(--line-height-snug);
  }

  .project-description {
    color: var(--secondary-foreground);
    font-size: var(--font-size-sm);
    line-height: var(--line-height-normal);
    margin-top: var(--space-3);
    max-width: 75%;
  }

  .conversations-list {
    border: var(--border-width-thin) solid var(--border);
    border-radius: var(--radius-md);
    padding: var(--space-2);
  }

  .conversation-row {
    position: relative;
    display: flex;
  }

  .conversation-row + .conversation-row {
    border-top: var(--border-width-thin) solid var(--border);
  }

  .conversation-link {
    display: block;
    width: 100%;
    --button-height: auto;
    --button-width: 100%;
  }

  .conversation-link::part(root) {
    justify-content: flex-start;
    text-align: left;
    white-space: normal;
    width: 100%;
  }

  .conversation-content {
    display: flex;
    flex-direction: column;
    gap: var(--space-0_5);
    width: 100%;
  }

  .conversation-title {
    font-weight: var(--font-weight-semibold);
    font-size: var(--font-size-sm);
    letter-spacing: var(--letter-spacing-tight);
    line-height: var(--line-height-snug);
    color: var(--foreground);
  }

  .conversation-meta {
    font-size: var(--font-size-xs);
    letter-spacing: var(--letter-spacing-normal);
    line-height: var(--line-height-snug);
    color: var(--muted-foreground);
    font-weight: var(--font-weight-regular);
  }

  .conversation-actions {
    position: absolute;
    right: var(--space-1);
    top: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    z-index: 1;
    opacity: 0;
    transition: opacity var(--duration-fast) var(--ease-out-3);
  }

  .conversation-row:hover .conversation-actions,
  .conversation-row:has(dui-menu[open]) .conversation-actions {
    opacity: 1;
  }

  .loading-view {
    /* Offset to match the toolbar height */
    padding-bottom: var(--space-14);
  }
`;

const conversationItemView = (
  conversation: Conversation,
  store: AppStore,
): TemplateResult => {
  const href = renderRoute(Config.PROJECT_CONVERSATION_URL, {
    project: conversation.project_id,
    conversation: conversation.id,
  });

  return html`
    <div class="conversation-row">
      <dui-button class="conversation-link" variant="ghost" href="${href}">
        <div class="conversation-content">
          <span class="conversation-title">${conversation.title}</span>
          <span class="conversation-meta">
            Last message ${toRelativeTime(conversation.updated_at)}
          </span>
        </div>
      </dui-button>
      <div class="conversation-actions">
        <conversation-menu
          .conversationId="${conversation.id}"
          .store="${store}"
        ></conversation-menu>
      </div>
    </div>
  `;
};

@customElement("project-view")
export class ProjectViewElement extends SignalWatcher(LitElement) {
  static override styles = [Styles.base, styles];

  @property({ attribute: false })
  accessor store: AppStore | undefined = undefined;

  #onSubmitInput = (event: CustomEvent<string>): void => {
    this.store?.send({ type: "create-conversation", message: event.detail });
  };

  #onDrop = (_event: CustomEvent<DropzoneDropDetail>): void => {
    // TODO: Handle dropped files
  };

  override render(): TemplateResult {
    if (!this.store) {
      return html``;
    }

    const state = this.store.get();

    if (isProjectLoading(state)) {
      return html`
        <div class="layout">
          <div class="layout-header">
            <app-toolbar></app-toolbar>
          </div>
          <div class="layout-main">
            <loading-view class="loading-view"></loading-view>
          </div>
        </div>
      `;
    }

    return html`
      <div class="layout">
        <div class="layout-header">
          <app-toolbar> </app-toolbar>
        </div>
        <div class="layout-main">
          <dui-page-inset max-width="40rem">
            <dui-vstack gap="1" class="project-header">
              <span class="section-label">Project</span>
              <dui-hstack justify="between">
                <h1>${state.projectTitle}</h1>
                <project-menu
                  .projectId="${state.projectId}"
                  .store="${this.store}"
                ></project-menu>
              </dui-hstack>
              ${state.projectDescription
                ? html`<p class="project-description">
                    ${state.projectDescription}
                  </p>`
                : ""}
            </dui-vstack>
            <dui-vstack gap="6">
              <ai-box
                placeholder="What strategic question are you exploring?"
                focus-token="${state.projectId}"
                @submit-input="${this.#onSubmitInput}"
              ></ai-box>
              ${state.conversations.length > 0
                ? html`<dui-vstack class="conversations-list" gap="2">
                    <span class="section-label">Conversations</span>
                    <!--<dui-dropzone @drop="${this.#onDrop}"></dui-dropzone>-->
                    ${repeat(
                      state.conversations.values(),
                      (conversation) => conversation.id,
                      (conversation) =>
                        conversationItemView(conversation, this.store!),
                    )}
                  </dui-vstack>`
                : ""}
            </dui-vstack>
          </dui-page-inset>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "project-view": ProjectViewElement;
  }
}
